import { v4 as uuidv4 } from "uuid";
import { makeAutoObservable, reaction } from "mobx";
import { ref, getDownloadURL, getStorage } from "firebase/storage";
import {
  formatNumber1Dec,
  formatNumber2Dec,
  formatTarget,
} from "../utils/numberUtils";
import { validateSerie } from "../utils/firestoreValidation";
import { showDevToast } from "../utils/devToast";

class Serie {
  updateSet = false;

  constructor(
    id = uuidv4(),
    order = 0,
    reps = 0,
    rpe = 0,
    weight = 0,
    targetReps = "",
    targetRpe = "",
    targetWeight = "",
    exercise = null,
    relevance = 3,
    hasVideo = null,
    requireVideo = false,
  ) {
    this.id = id;
    this.order = order;
    this.reps = reps;
    this.rpe = rpe;
    this.weight = weight;
    this.targetReps = formatTarget(targetReps);
    this.targetRpe = formatTarget(targetRpe);
    this.targetWeight = formatTarget(targetWeight);
    this.exercise = exercise;
    this.relevance = relevance;
    this.hasVideo = hasVideo;
    this.requireVideo = requireVideo;
    makeAutoObservable(this);

    reaction(
      () => ({
        // id: this.id,
        order: this.order,
        reps: this.reps,
        rpe: this.rpe,
        weight: this.weight,
        target_reps: this.targetReps,
        target_rpe: this.targetRpe,
        target_weight: this.targetWeight,
        relevance: this.relevance,
        require_video: this.requireVideo,
      }),

      () => {
        console.log("Reaction Serie");

        if (!this.exercise) return;

        const { workout, block } = this.exercise;

        if (workout) {
          const { block, coach } = workout;

          if (block?.client) {
            const { client } = block;
            if (!client.avoidTransaction && client.canQueueBlockTransactions) {
              workout.setProperty("hasChanged", true);
              client.updateClientTransactionData(block.id);
              client.startOrResetBatchTimer();
            }
          } else if (block?.coach) {
            block.coach.updateCoachTransactionBlocks(block.id.toLowerCase());
            block.coach.startOrResetBatchTimer();
          } else if (coach) {
            workout.setProperty("hasChanged", true);
            coach.updateCoachTransactionWorkouts(workout.id);
            coach.startOrResetBatchTimer();
          }
        } else if (block) {
          if (
            block.client?.canQueueBlockTransactions &&
            !block.client.avoidTransaction
          ) {
            block.client.updateClientTransactionData(block.id);
            block.client.startOrResetBatchTimer();
          } else if (block.coach) {
            block.coach.updateCoachTransactionBlocks(block.id.toLowerCase());
            block.coach.startOrResetBatchTimer();
          }
        }
      },
    );
  }

  // #region /* CHAT */
  async getAttachmentURL() {
    // Construct the filename and path
    const fileName = `${this.id}.mp4`;
    const client = this.exercise?.workout?.block?.client;
    if (!client) return null;

    const storagePath = `coaches/${client.coach.id}/chats/${client.id}/video_messages/${fileName}`;

    try {
      // Create a reference to the file
      const storage = getStorage(undefined, "gs://efort-app-storage-eu"); // This will use default app and region

      const fileRef = ref(storage, storagePath);
      // Get the download URL
      const url = await getDownloadURL(fileRef);
      return url;
    } catch (error) {
      console.error("Error retrieving download URL:", error);
      return null;
    }
  }
  // #endregion

  // #region /* STATS */
  get getMaxErm() {
    const rirOrRpe = this.exercise ? this.exercise.getRirOrRpe : false;

    let eReps;
    if (rirOrRpe) {
      // RIR
      eReps = this.reps + this.rpe;
    } else {
      // RPE
      eReps = this.reps + (10.0 - this.rpe);
    }

    const serieErm =
      ((this.weight * 100) / (48.8 + 53.8 * Math.exp(-0.06 * eReps))) * 0.99466;

    return serieErm;
  }
  // #endregion

  // #region /* FORMAT & TEXTBOX ACTIONS */
  getFormattedText(rirOrRpe, exerciseUnit) {
    const weight = this?.weight;
    const reps = this?.reps;
    const rpe = this?.rpe;

    return (
      `${formatNumber1Dec(weight)} ${exerciseUnit ? "lbs" : "kg"}` +
      ` x ${formatNumber1Dec(reps)}` +
      ` ${rirOrRpe ? "RIR" : "@"}${formatNumber1Dec(rpe)}`
    );
  }

  getFormattedTextParts(rirOrRpe, exerciseUnit, defaultBlack = true) {
    const weight = this?.weight;
    const reps = this?.reps;
    const rpe = this?.rpe;

    const weightText = `${formatNumber1Dec(weight)} ${
      exerciseUnit ? "lbs" : "kg"
    }`;
    const repsText = ` x ${formatNumber1Dec(reps)} `;
    const rpeLabel = rirOrRpe ? "RIR" : "@";
    const rpeValue = `${formatNumber1Dec(rpe)}`;

    return {
      weightText,
      repsText,
      rpeLabel,
      rpeValue,
      rpeColor: this.getBackgroundColor(defaultBlack),
    };
  }

  getBackgroundColor(defaultBlack = true) {
    const target = this.getTargetRpeReference;
    const actual = this.rpe;

    if (typeof target === "number" && target > 0.02 && actual > 0.02) {
      // round both to one decimal (to match your formatNumber logic)
      const a = parseFloat(formatNumber2Dec(actual));
      const t = parseFloat(formatNumber2Dec(target));

      if (Math.abs(a - t) >= 1) {
        return a > t ? "rgba(240, 68, 56, 1)" : "rgba(15, 136, 21, 1)";
      }
    }

    return defaultBlack ? "rgba(0, 0, 0, 1)" : "rgba(255, 255, 255, 1)";
  }

  get getTargetWeightReference() {
    if (typeof this.targetWeight !== "string") return null;
    // match integers or decimals ending in .25 or .5
    const regex = /\b\d+(?:\.(?:25|5))?\b/g;
    const matches = this.targetWeight.match(regex);
    if (!matches) return null;
    // parse floats, keep only > 0, then pick the max
    const values = matches.map((m) => parseFloat(m)).filter((n) => n > 0);
    return values.length > 0 ? Math.max(...values) : null;
  }

  get getTargetRepsReference() {
    if (typeof this.targetReps !== "string") return null;
    // match any sequence of digits (0, 1, 2, …)
    const regex = /\b\d+\b/g;
    const matches = this.targetReps.match(regex);
    if (!matches) return null;
    // parse to ints and return the max
    const values = matches.map((m) => parseInt(m, 10));
    return Math.max(...values);
  }

  get getTargetRpeReference() {
    if (typeof this.targetRpe !== "string") return null;
    // match 0–9 or 10, optionally followed by .0 or .5
    const regex = /\b(?:[0-9]|10)(?:\.(?:0|5))?\b/g;
    const matches = this.targetRpe.match(regex);
    if (!matches) return null;
    // parse and return the highest one
    const values = matches.map((m) => parseFloat(m));
    return Math.max(...values);
  }

  copyTargetToActual() {
    // Only allow “pure” numbers (optionally signed, optionally decimal)
    const FLOAT_REGEX = /^[+-]?\d+(\.\d+)?$/;
    // Only allow unsigned integers
    const INT_REGEX = /^\d+$/;

    if (this.targetWeight != null) {
      const str = String(this.targetWeight).trim();
      if (FLOAT_REGEX.test(str)) {
        this.setProperty("weight", parseFloat(str));
      }
    }

    if (this.targetReps != null) {
      const str = String(this.targetReps).trim();
      if (INT_REGEX.test(str)) {
        this.setProperty("reps", parseInt(str, 10));
      }
    }

    if (this.targetRpe != null) {
      const str = String(this.targetRpe).trim();
      if (FLOAT_REGEX.test(str)) {
        this.setProperty("rpe", parseFloat(str));
      }
    }
  }
  // #endregion

  // #region /* REAL-TIME */
  updateSerie(serie, hasChanged) {
    if (!hasChanged) {
      this.order = serie.order ?? this.order;
      this.targetReps = formatTarget(serie.target_reps);
      this.targetRpe = formatTarget(serie.target_rpe);
      this.targetWeight = formatTarget(serie.target_weight);
      this.relevance = serie.relevance ?? 3;
      this.requireVideo = serie.require_video ?? false;
    }

    this.hasVideo = serie.has_video ?? false;
    this.reps = serie.reps ?? 0;
    this.rpe = serie.rpe ?? 0.0;
    this.weight = serie.weight ?? 0.0;
    this.updateSet = !this.updateSet;
  }

  uploadToCloud() {
    const data = {
      id: this.id.toString().toLowerCase(),
      order: this.order,
      reps: this.reps,
      rpe: this.rpe,
      weight: this.weight,
      target_reps: this.targetReps,
      target_rpe: this.targetRpe,
      target_weight: this.targetWeight,
      relevance: this.relevance,
      ...(this.hasVideo && { has_video: this.hasVideo }),
      ...(this.requireVideo && { require_video: this.requireVideo }),
    };

    // Validate data (always runs, lightweight)
    // Only show toast in development
    if (process.env.NODE_ENV === "development") {
      const validation = validateSerie(data);
      if (!validation.valid) {
        const errorMsg = validation.errors.join("; ");
        showDevToast("Serie validation failed", errorMsg);
      }
    }

    return data;
  }
  // #endregion

  // #region /* HELPERS */
  get getBlockDashboardShowingActual() {
    if (this.exercise.exercise) {
      if (this.exercise.exercise.block) {
        return this.exercise.exercise.block.showingDashboardActual;
      }
      return false;
    }

    if (this.exercise.block) {
      return this.exercise.block.showingDashboardActual;
    } else if (this.exercise.workout.block) {
      return this.exercise.workout.block.showingDashboardActual;
    } else {
      return false;
    }
  }

  setProperty(propertyName, value) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      this[propertyName] = value;
    } else {
      console.error(`Property ${propertyName} does not exist.`);
    }
  }

  getProperty(propertyName) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      return this[propertyName];
    } else {
      console.error(`Property ${propertyName} does not exist.`);
      return undefined;
    }
  }
  // #endregion
}
export default Serie;
